import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import { hhmm } from "../../utils/hora";

// La proxima reunion, en la cabecera.
//
// El calendario ya estaba en ALM, pero solo se veia yendo a buscarlo: la pestana
// del mes, o el buscador de al enlazar. Lo que hace falta saber sin ir a ninguna
// parte es lo de dentro de un rato —a las y media hay una reunion—, y eso es
// justo lo que no estaba en pantalla.
//
// Los eventos son los que ya tiene App (`outlookEvents`, el cache de Postgres):
// no se pide nada nuevo. Y si no queda nada por delante hoy, este boton NO SE
// PINTA. Un indicador que solo sirve para decir que no tiene nada que decir es
// ruido permanente en el sitio mas visible de la aplicacion.

// ── Las reglas, puras y aparte para poder probarlas ──────────────────────────

// El dia de Madrid de un instante, "2026-09-16". En-CA da justo ese orden.
const diaMadrid = d => new Date(d).toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });

const MIN = 60000;
const DURACION_SUPUESTA = 30 * MIN;

// Lo que queda por delante HOY, de lo mas proximo a lo mas lejano.
//
// Una reunion EMPEZADA sigue contando: es precisamente la que interesa ver, y la
// que se va a cronometrar. Se cae de la lista cuando termina, no cuando empieza.
//
// Solo hoy. Lo de manana no es un aviso, es una agenda, y para eso esta la
// pestana del calendario.
export function proximas(eventos, ahora = new Date()) {
  const hoy = diaMadrid(ahora);
  const t = ahora.getTime();
  return (eventos || [])
    .filter(e => e && e.start_datetime && !e.is_all_day)
    .filter(e => diaMadrid(e.start_datetime) === hoy)
    .filter(e => {
      // Sin hora de fin se le supone la duracion tipica de una reunion: mejor
      // que descartarla en el minuto en que empieza.
      const fin = e.end_datetime
        ? new Date(e.end_datetime).getTime()
        : new Date(e.start_datetime).getTime() + DURACION_SUPUESTA;
      return fin > t;
    })
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
}

// Cuanto falta, dicho como lo diria una persona: "en 0 min" no lo dice nadie, y
// "en 225 min" hay que dividirlo mentalmente.
export function cuanto(inicio, fin, ahora = new Date()) {
  const min = Math.round((new Date(inicio).getTime() - ahora.getTime()) / MIN);
  if (min <= 0) {
    const restan = fin ? Math.round((new Date(fin).getTime() - ahora.getTime()) / MIN) : 0;
    return restan > 0 ? `ahora · quedan ${restan} min` : "ahora";
  }
  if (min < 60) return `en ${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `en ${h} h ${m} min` : `en ${h} h`;
}

// El color es la unica parte que se lee sin mirar, asi que solo cambia cuando
// hay algo que hacer: rojo si ya ha empezado, ambar si esta encima, y el azul
// del calendario el resto del tiempo.
export function color(inicio, ahora = new Date()) {
  const min = (new Date(inicio).getTime() - ahora.getTime()) / MIN;
  if (min <= 0) return "var(--red)";
  if (min <= 10) return "#f59e0b";
  return "#3b82f6";
}

// ── El boton ─────────────────────────────────────────────────────────────────

export default function ProximaReunionHeaderButton({ eventos = [], onVerCalendario }) {
  const [abierto, setAbierto] = useState(false);
  const [ahora, setAhora] = useState(() => new Date());
  const ref = useRef(null);

  // El boton dice LA HORA, no lo que falta, asi que no hay ningun numero que
  // haya que mover minuto a minuto. El reloj solo esta para lo que si cambia
  // solo: que una reunion terminada salga de la lista, y que el color pase a
  // ambar y a rojo cuando toca. Con cinco minutos sobra para las dos cosas.
  //
  // Abierto es otra cosa: ahi el desplegable si cuenta minutos, y una cifra
  // parada cinco minutos delante de los ojos se lee como un fallo. Mientras
  // esta abierto va a 30 s, y eso dura lo que dura una mirada.
  useEffect(() => {
    const cada = abierto ? 30000 : 5 * 60000;
    const id = setInterval(() => setAhora(new Date()), cada);
    return () => clearInterval(id);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const pendientes = proximas(eventos, ahora);
  const siguiente = pendientes[0];

  // OJO: el return temprano va DESPUES de todos los hooks. Ponerlo antes cambia
  // el numero de hooks entre renders y React lanza el error 310.
  if (!siguiente) return null;

  const cl = color(siguiente.start_datetime, ahora);
  const falta = cuanto(siguiente.start_datetime, siguiente.end_datetime, ahora);

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button className="btn" onClick={() => setAbierto(p => !p)}
        title={`${siguiente.title || "Reunión"} · ${hhmm(siguiente.start_datetime, "")} · ${falta}`}
        style={{
          padding: "6px 12px", borderRadius: 8, border: `1px solid ${cl}55`,
          background: abierto ? `${cl}1f` : "var(--surface)",
          color: "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center",
          gap: 7, fontSize: 11.5, fontFamily: "var(--font)", maxWidth: 270, minWidth: 0,
        }}>
        <Icon name="calendar-week" size={13} color={cl} />
        <span className="m-hide" style={{ fontWeight: 700, color: cl, flexShrink: 0,
          fontVariantNumeric: "tabular-nums" }}>{hhmm(siguiente.start_datetime, "")}</span>
        <span className="m-hide" style={{ color: "var(--text3)", overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {siguiente.title || "Reunión"}
        </span>
      </button>

      {abierto && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 60,
          width: "min(320px, calc(100vw - 32px))", background: "var(--surface)",
          border: "1px solid var(--border)", borderRadius: 11, boxShadow: "var(--shadow-lg)",
          padding: 12, display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: ".06em" }}>
            Lo que queda hoy
          </div>

          {pendientes.slice(0, 6).map((e, i) => (
            <div key={e.external_id} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <span style={{ fontSize: 11, fontFamily: "var(--mono)", flexShrink: 0, paddingTop: 1,
                color: i === 0 ? color(e.start_datetime, ahora) : "var(--text3)",
                fontWeight: i === 0 ? 700 : 500 }}>
                {hhmm(e.start_datetime, "")}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: i === 0 ? 600 : 500, color: "var(--text)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.title || "Reunión"}
                </div>
                <div style={{ fontSize: 10, color: "var(--text3)", overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cuanto(e.start_datetime, e.end_datetime, ahora)}
                  {e.location ? ` · ${e.location}` : ""}
                </div>
              </div>
            </div>
          ))}

          {siguiente.web_link && (
            <a href={siguiente.web_link} target="_blank" rel="noreferrer"
              style={{ fontSize: 11.5, color: "var(--accent)", textDecoration: "none",
                display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="box-arrow-up-right" size={11} color="var(--accent)" />
              Abrir en Outlook
            </a>
          )}

          {onVerCalendario && (
            <button className="btn" onClick={() => { setAbierto(false); onVerCalendario(); }}
              style={{ padding: "7px 0", borderRadius: 8, border: "1px solid var(--border)",
                background: "var(--surface2)", color: "var(--text2)", cursor: "pointer",
                fontSize: 11.5, fontFamily: "var(--font)", display: "flex",
                alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Icon name="calendar-week" size={12} color="var(--text2)" />
              Ver el calendario
            </button>
          )}
        </div>
      )}
    </div>
  );
}
